import React, { useState, useEffect } from 'react';
import { Input, DatePicker, Table, Avatar } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchRooms } from '../../state/actions/room';
import { fetchBookings } from '../../state/actions/booking';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import './BookingHistory.css';

const { RangePicker } = DatePicker;

const BookingHistory = () => {
  const dispatch = useDispatch();
  const { siteId } = useParams();
  const [search, setSearch] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'days'),
    dayjs()
  ]);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [roomSearchTerm, setRoomSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { rooms, bookings, loading, activeTab, totalCount } = useSelector((state) => ({
    rooms: state.room?.rooms || [],
    bookings: state.booking?.bookings || [],
    loading: state.booking?.loading || false,
    activeTab: state.tab?.tab,
    totalCount: state.booking?.totalCount || 0,
  }));

  // Fetch rooms when component mounts
  useEffect(() => {
    if (siteId && activeTab === 'bookingManagement') {
      dispatch(fetchRooms({ siteId }));
    }
  }, [dispatch, siteId, activeTab]);

  // Fetch bookings when filters change
  useEffect(() => {
    if (siteId && dateRange && dateRange[0] && dateRange[1]) {
      const roomIdFilter = selectedRoom !== 'all' ? selectedRoom : undefined;
      dispatch(fetchBookings({
        siteId,
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        search: search,
        roomId: roomIdFilter,
        page: currentPage,
        limit: pageSize
      }));
    }
  }, [dispatch, siteId, activeTab, selectedRoom, dateRange, search, currentPage, pageSize]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleRoomSelect = (roomId) => {
    setSelectedRoom(roomId);
    setPlaylistOpen(false);
    setCurrentPage(1); // Reset to first page when changing room
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setCurrentPage(1); // Reset to first page when changing date range
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  // Filter rooms based on search term
  const filteredRooms = rooms.filter(room =>
    room.name.toLowerCase().includes(roomSearchTerm.toLowerCase()) ||
    room._id.toLowerCase().includes(roomSearchTerm.toLowerCase())
  );

  // Table columns
  const columns = [
    {
      title: 'No.',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Room Name',
      dataIndex: 'roomIds',
      key: 'roomName',
      render: (roomIds) => {
        if (roomIds && roomIds.length > 0) {
          return roomIds.map(room => room.name).join(', ');
        }
        return '-';
      },
    },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy) => createdBy,
    },
    {
      title: 'POI',
      dataIndex: 'roomIds',
      key: 'poi',
      render: (roomIds) => {
        if (roomIds && roomIds.length > 0) {
          const poiNames = [];
          roomIds.forEach(room => {
            if (room.poiIds && room.poiIds.length > 0) {
              room.poiIds.forEach(poi => {
                if (poi.name && !poiNames.includes(poi.name)) {
                  poiNames.push(poi.name);
                }
              });
            }
          });
          return poiNames.length > 0 ? poiNames.join(', ') : '-';
        }
        return '-';
      },
    },
    {
      title: 'PIC',
      dataIndex: 'picName',
      key: 'picName',
      render: (picName) => picName || '-',
    },
    {
      title: 'Start Time (SGT)',
      dataIndex: 'startTimeSGT',
      key: 'startTime',
      render: (startTime) => {
        if (startTime) {
          return moment(startTime, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm');
        }
        return '-';
      },
      sorter: (a, b) => moment(a.startTimeSGT, 'YYYY-MM-DD HH:mm:ss').unix() - moment(b.startTimeSGT, 'YYYY-MM-DD HH:mm:ss').unix(),
    },
    {
      title: 'End Time (SGT)',
      dataIndex: 'endTimeSGT',
      key: 'endTime',
      render: (endTime) => {
        if (endTime) {
          return moment(endTime, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm');
        }
        return '-';
      },
      sorter: (a, b) => moment(a.endTimeSGT, 'YYYY-MM-DD HH:mm:ss').unix() - moment(b.endTimeSGT, 'YYYY-MM-DD HH:mm:ss').unix(),
    },
  ];

  return (
    <div className="booking-history">
      <div className="booking-history-header">
        <h2>Booking History</h2>
      </div>

      <div className="booking-history-filters">
        {/* Search */}
        <div className="filter-item">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={search}
            onChange={handleSearch}
            style={{ width: 250 }}
          />
        </div>

        {/* Room Dropdown */}
        <div className="filter-item">
          <div className="playlist-wrapper" onClick={() => setPlaylistOpen(!playlistOpen)}>
            <div className="playlist-icon">
              <i className="feather-layers"></i>
            </div>
            <span className="playlist-text">
              {selectedRoom === 'all' ? 'All Rooms' : (rooms.find(r => r._id === selectedRoom)?.name || 'All Rooms')}
            </span>
            <div className={`playlist-chevron ${playlistOpen ? 'open' : ''}`}>
              <i className="feather-chevron-down"></i>
            </div>
            {playlistOpen && (
              <div className="playlist-dropdown">
                {/* Search within dropdown */}
                <div className="room-search-wrapper">
                  <Input
                    placeholder="Search rooms..."
                    prefix={<SearchOutlined />}
                    value={roomSearchTerm}
                    onChange={(e) => setRoomSearchTerm(e.target.value)}
                    className="room-search-input"
                  />
                </div>

                {/* All Rooms option */}
                <div
                  className={`room-option ${selectedRoom === 'all' ? 'selected' : ''}`}
                  onClick={() => handleRoomSelect('all')}
                >
                  <Avatar size={48} className="room-avatar">
                    <i className="feather-layers" />
                  </Avatar>
                  <div className="room-info">
                    <div className="room-name">All Rooms</div>
                    <div className="room-id">View all rooms</div>
                  </div>
                </div>

                {/* Room options */}
                {filteredRooms.map(room => (
                  <div
                    key={room._id}
                    className={`room-option ${selectedRoom === room._id ? 'selected' : ''}`}
                    onClick={() => handleRoomSelect(room._id)}
                  >
                    <Avatar
                      size={48}
                      src={room.image}
                      className="room-avatar"
                    >
                      {!room.image && <i className="feather-image" />}
                    </Avatar>
                    <div className="room-info">
                      <div className="room-name">{room.name}</div>
                      <div className="room-id">ID: {room._id.slice(-8)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="filter-item">
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="DD/MM/YYYY"
            placeholder={['Start Date', 'End Date']}
            style={{ width: 250 }}
          />
        </div>
      </div>

      <div className="booking-history-table">
        <Table
          columns={columns}
          dataSource={bookings}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} data`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePaginationChange,
            onShowSizeChange: handlePaginationChange,
          }}
          scroll={{ x: 800 }}
        />
      </div>
    </div>
  );
};

export default BookingHistory;
